import * as React from 'react';

import { FormField } from 'components/form';
import Tooltip from 'components/Tooltip';
import Overlay from 'components/overlay/Overlay';

import SOVQueriesInputField from 'modules/insights/sov/components/queries/inputs/field/SOVQueriesInputField';
import SOVQueriesInputRadio from 'modules/insights/sov/components/queries/inputs/radio/SOVQueriesInputRadio';

import { __t, applyTranslations } from 'utils/language';
import {
  MIN_CHAR_COUNT_FOR_QUERY,
  MULTI,
  PRIMARY,
  SECONDARY,
  SINGLE
} from 'modules/insights/sov/utils/queries/sov-queries';

import './sov-queries-modal-step-search-terms.scss';

import type {
  SOVGeneralModalTextFields,
  SOVQueryLocal,
  SOVQueryStepType,
  SOVQueryType,
  SOVReportType
} from 'modules/insights/sov/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/components/queries/modals/sov-queries-modal.i18n.json'));

type Props = {
  reportType: SOVReportType,
  query: SOVQueryLocal,
  isMulti: boolean,
  isEditMode: boolean,
  textFields: SOVGeneralModalTextFields,
  onTextFieldChange: (fieldType: SOVQueryStepType, value: string) => void,
  onRadioButtonChange: (value: SOVQueryType) => void,
  onSetActionButtonDisabledStatus: (status: boolean) => void
};

const COLLABARY_MSG_KEY = 'collabary_common';
const MODAL_MSG_KEY = 'sov_queries_modal';

class SOVQueriesModalStepSearchTerms extends React.PureComponent<Props> {
  radioRefSingle: HTMLInputElement | undefined | null = null;
  radioRefMulti: HTMLInputElement | undefined | null = null;

  componentDidMount() {
    this.getIsActionButtonDisabledStatus();
    this.handleActivateSectionOnLoad();
  }

  componentDidUpdate() {
    this.getIsActionButtonDisabledStatus();
  }

  componentWillUnmount() {
    const { onSetActionButtonDisabledStatus } = this.props;

    onSetActionButtonDisabledStatus(true);
  }

  getMode(fieldType: string): boolean {
    const { isMulti } = this.props;

    return fieldType === SINGLE ? !isMulti : isMulti;
  }

  getInputValue(fieldType: string): string {
    const { textFields } = this.props;
    const isMulti = fieldType === PRIMARY || fieldType === SECONDARY;

    if (fieldType === SINGLE && this.getMode(SINGLE)) {
      return textFields[SINGLE];
    }

    if (isMulti && this.getMode(MULTI)) {
      return textFields[fieldType];
    }

    return '';
  }

  getIsDisabledStatus(type: 'field' | 'radio', isChecked: boolean): boolean {
    const { isEditMode } = this.props;

    // combining `isEditMode` and `!isChecked` prevents type conversion (e.g. single -> multi and vice-versa)
    if (type === 'radio') {
      return isEditMode && !isChecked;
    }

    return !isChecked;
  }

  getIsActionButtonDisabledStatus(): void {
    const { query, textFields, onSetActionButtonDisabledStatus } = this.props;
    let result = false;

    if (this.getMode(SINGLE)) {
      const singleText = query.name || textFields.single;

      result = singleText.length < MIN_CHAR_COUNT_FOR_QUERY;
    }

    if (this.getMode(MULTI)) {
      const primaryText = query.names?.primary || textFields.primary;
      const secondaryText = query.names?.secondary || textFields.secondary;

      result = primaryText.length < MIN_CHAR_COUNT_FOR_QUERY || secondaryText.length < MIN_CHAR_COUNT_FOR_QUERY;
    }

    onSetActionButtonDisabledStatus(result);
  }

  handleRefCallbackSingle = (ref?: HTMLInputElement | null): void => {
    this.radioRefSingle = ref;
  };

  handleRefCallbackMulti = (ref?: HTMLInputElement | null): void => {
    this.radioRefMulti = ref;
  };

  handleActivateSectionOnLoad(): void {
    if (this.getMode(SINGLE) && this.radioRefSingle) {
      this.radioRefSingle.click();
      return;
    }

    if (this.getMode(MULTI) && this.radioRefMulti) {
      this.radioRefMulti.click();
    }
  }

  handleActivateSectionOnClick = (event: SyntheticEvent<HTMLElement>): void => {
    const target = event.currentTarget;
    const type = target.dataset.type || null;

    if (type === SINGLE && this.radioRefSingle) {
      this.radioRefSingle.click();
      return;
    }

    if (type === MULTI && this.radioRefMulti) {
      this.radioRefMulti.click();
    }
  };

  renderOverlay(containerId: string, queryType: SOVQueryType): React.ReactNode {
    const convertType = queryType === SINGLE ? SINGLE : MULTI;

    return (
      <Overlay
        parentId={containerId}
        text={__t(`${MODAL_MSG_KEY}.messages.overlay`, {
          type: __t(`${MODAL_MSG_KEY}.labels.${convertType}`)
        })}
        className="sov-queries-modal-step-search-terms__overlay"
        showOnHover
      />
    );
  }

  renderRequiredMark(): React.ReactNode {
    return (
      <div className="sov-queries-modal-step-search-terms__required form-required-mark">
        *
      </div>
    );
  }

  renderTooltip(queryType: SOVQueryType): React.ReactNode {
    const { reportType } = this.props;

    return (
      <div className="sov-queries-modal-step-search-terms__tooltip">
        <Tooltip
          message={__t(`${MODAL_MSG_KEY}.tooltips.enter_${queryType}`, {
            channel: __t(`${COLLABARY_MSG_KEY}.social.${reportType}`)
          })}
          width="long"
          showQuestionMark
        />
      </div>
    );
  }

  renderRadioButton(queryType: SOVQueryType, fieldType: string): React.ReactNode {
    const { onRadioButtonChange } = this.props;
    const isChecked = this.getMode(fieldType);

    return (
      <div className="sov-queries-modal-step-search-terms__radio-button">
        <SOVQueriesInputRadio
          key={`sov-queries-modal-step-search-terms__radio-button--${queryType}`}
          queryType={queryType}
          isChecked={isChecked}
          isDisabled={this.getIsDisabledStatus('radio', isChecked)}
          onChange={onRadioButtonChange}
          onRefCallbackSingle={this.handleRefCallbackSingle}
          onRefCallbackMulti={this.handleRefCallbackMulti}
        />
      </div>
    );
  }

  renderTextField(queryType: SOVQueryType, fieldType: string, useRefCallback: boolean): React.ReactNode {
    const { onTextFieldChange } = this.props;
    const isChecked = this.getMode(fieldType);
    const autoFocus = useRefCallback && isChecked;
    const value = this.getInputValue(fieldType);

    return (
      <div className="sov-queries-modal-step-search-terms__text-field">
        <SOVQueriesInputField
          key={`sov-queries-modal-step-search-terms__text-field--${fieldType}-${String(autoFocus)}`}
          queryType={queryType}
          fieldType={fieldType}
          value={value}
          isChecked={isChecked}
          isDisabled={this.getIsDisabledStatus('field', isChecked)}
          autoFocus={autoFocus}
          useRefCallback={useRefCallback}
          onChange={onTextFieldChange}
        />
      </div>
    );
  }

  renderSingle(): React.ReactNode {
    const { isEditMode } = this.props;
    const isChecked = this.getMode(SINGLE);
    const id = 'sov-queries-modal-step-search-terms__single';

    return (
      <div
        id={id}
        className="sov-queries-modal-step-search-terms__section"
      >
        <div className="sov-queries-modal-step-search-terms__title">
          {this.renderRadioButton(SINGLE, SINGLE)}
          {this.renderRequiredMark()}
          {this.renderTooltip(SINGLE)}
        </div>
        <FormField
          direction="column"
          label=""
          showValidationIcon={false}
          onClick={this.handleActivateSectionOnClick}
          data={{ type: SINGLE }}
        >
          {this.renderTextField(SINGLE, SINGLE, isChecked)}
        </FormField>
        {
          isEditMode && !isChecked &&
            this.renderOverlay(id, SINGLE)
        }
      </div>
    );
  }

  renderMulti(): React.ReactNode {
    const { isEditMode } = this.props;
    const isChecked = this.getMode(MULTI);
    const id = 'sov-queries-modal-step-search-terms__multi';

    return (
      <div
        id={id}
        className="sov-queries-modal-step-search-terms__section"
      >
        <div className="sov-queries-modal-step-search-terms__title">
          {this.renderRadioButton(MULTI, PRIMARY)}
          {this.renderRequiredMark()}
          {this.renderTooltip(MULTI)}
        </div>
        <FormField
          direction="column"
          label=""
          showValidationIcon={false}
          onClick={this.handleActivateSectionOnClick}
          data={{ type: MULTI }}
        >
          {this.renderTextField(MULTI, PRIMARY, isChecked)}
          {this.renderTextField(MULTI, SECONDARY, false)}
        </FormField>
        {
          isEditMode && !isChecked &&
            this.renderOverlay(id, MULTI)
        }
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="sov-queries-modal-step-search-terms">
        {this.renderSingle()}
        {this.renderMulti()}
      </div>
    );
  }
}

export default SOVQueriesModalStepSearchTerms;
